import { useState } from 'react';
import axios from 'axios';
import { Alert, Button, Code, Select, Stack, Text, Title } from '@mantine/core';
import {
  useSuiteReleaseHistory,
  useSuiteReleaseStatus,
  useSuiteRelease,
  useReleaseReadiness,
  useReleaseDocuments,
} from '@/hooks/api/automationSuites/useSuiteReleases.ts';
import CompositionWorkspace from '@/pages/Editor/components/Composition/CompositionWorkspace.tsx';
import { compositionOptions } from '@/pages/Editor/components/Composition/compositionOptions.ts';
import PublicationHistory from '@/pages/Editor/components/Publication/PublicationHistory.tsx';
import ReleaseDocumentationReview from '@/pages/Editor/components/Publication/ReleaseDocumentationReview.tsx';
import type { SuiteDetail } from '@/types/automationSuites.ts';
import { SuiteError } from '@/pages/AutomationSuites/SuiteShared.tsx';
import {
  ExactDetails,
  ImplementationLabel,
  TriggerLabel,
} from '@/pages/Editor/components/Composition/CompositionPresentation.tsx';

export default function SuiteReleases({
  suite,
  canEdit,
  refresh,
}: {
  suite: SuiteDetail;
  canEdit: boolean;
  refresh: () => void;
}) {
  const status = useSuiteReleaseStatus(suite.id, suite.shared_text_available);
  const history = useSuiteReleaseHistory(suite.id, suite.project_id, suite.shared_text_available);
  const [selected, setSelected] = useState<string | null>(null);
  const [authoring, setAuthoring] = useState(false);
  const [historyAutomation, setHistoryAutomation] = useState<string | null>(null);
  if (!suite.shared_text_available)
    return <Alert color="orange">Release documentation is unavailable in this access scope.</Alert>;
  const historyDenied =
    axios.isAxiosError(history.error) && [401, 403].includes(history.error.response?.status ?? 0);
  if (status.isError || historyDenied)
    return (
      <SuiteError
        error={status.error ?? history.error}
        retry={() => {
          if (status.isError) void status.refetch();
          if (history.isError) void history.refetch();
        }}
      />
    );
  if (!status.isFetchedAfterMount) return <Text>Loading Suite candidates…</Text>;
  const releases =
    (history.isFetchedAfterMount && !history.isError ? history.data : undefined)?.pages.flatMap(
      (page) => page.releases,
    ) ?? [];
  const latestReleaseId =
    releases.find(
      (release) => release.id === status.data?.latest_release_id && release.record_valid,
    )?.id ?? null;
  const id = selected ?? latestReleaseId;
  return (
    <Stack>
      <Title order={2}>Suite coordination</Title>
      <Text>
        A Suite provides shared membership and Blueprint context for exact candidates. Each
        Automation entrypoint has its own published selection; this Suite has no separate
        publication authority.
      </Text>
      <Title order={3}>Current entrypoint publication</Title>
      {status.data?.active_entrypoints?.map((entry) => (
        <Stack key={entry.automation_id} gap={4}>
          <Text>
            {suite.members.results.find((member) => member.automation_id === entry.automation_id)
              ?.name ?? `Automation name unavailable (${entry.automation_id})`}
            {' · '}
            {entry.active.published_release_id ? 'Published' : 'Unpublished'}
          </Text>
          {entry.active.active_roots.map((root) => (
            <Text key={root.trigger_step_id} size="sm">
              <ImplementationLabel version={root} />
              {' · '}
              <TriggerLabel
                versionId={root.automation_version_id}
                triggerId={root.trigger_step_id}
              />
            </Text>
          ))}
          <ExactDetails
            summary="Published selection details"
            values={{
              'Automation ID': entry.automation_id,
              'Published candidate ID': entry.active.published_release_id,
              'Publication revision': String(entry.active.head_revision),
            }}
          />
          <Text size="sm">
            Candidate alignment:{' '}
            {
              {
                no_selection: 'No candidate selection',
                inactive: 'Unpublished',
                aligned: 'Matches candidate',
                drifted: 'Differs from candidate',
              }[entry.alignment]
            }
          </Text>
          <Button
            variant="subtle"
            onClick={() =>
              setHistoryAutomation((previous) =>
                previous === entry.automation_id ? null : entry.automation_id,
              )
            }
          >
            {historyAutomation === entry.automation_id ? 'Close' : 'Review'} publication history for{' '}
            {suite.members.results.find((member) => member.automation_id === entry.automation_id)
              ?.name ?? entry.automation_id}
          </Button>
          {historyAutomation === entry.automation_id && (
            <PublicationHistory
              automationId={entry.automation_id}
              canEdit={canEdit && !suite.archived_at}
            />
          )}
        </Stack>
      ))}
      {canEdit && !suite.archived_at && (
        <Button
          variant={authoring ? 'subtle' : 'filled'}
          onClick={() => setAuthoring((value) => !value)}
        >
          {authoring ? 'Close coordinated candidate' : 'Start coordinated candidate'}
        </Button>
      )}
      <Title order={3}>Coordinated candidates</Title>
      <Select
        label="Open candidate or historical Release"
        searchable
        disabled={!history.isFetchedAfterMount || history.isError}
        value={id ?? null}
        onChange={(value) => {
          setSelected(value);
          setAuthoring(false);
        }}
        data={compositionOptions(releases, id)}
      />
      {history.isError && (
        <Alert color="red">
          Candidate history unavailable.
          <Button
            variant="subtle"
            disabled={history.isFetching}
            onClick={() =>
              void (history.isFetchNextPageError ? history.fetchNextPage() : history.refetch())
            }
          >
            Retry candidate history
          </Button>
        </Alert>
      )}
      {history.hasNextPage && (
        <Button
          variant="subtle"
          disabled={!history.isFetchedAfterMount || history.isError || history.isFetching}
          onClick={() => void history.fetchNextPage()}
        >
          Load older Releases
        </Button>
      )}
      <Button
        variant="default"
        onClick={() => {
          void status.refetch();
          void history.refetch();
          refresh();
        }}
      >
        Refresh candidates
      </Button>
      {authoring ? (
        <CompositionWorkspace
          key={suite.id}
          suite={suite}
          canEdit={canEdit && !suite.archived_at}
          onSaved={(releaseId) => {
            setSelected(releaseId);
            setAuthoring(false);
            void status.refetch();
            void history.refetch();
            refresh();
          }}
        />
      ) : id ? (
        <SuiteCandidate
          key={id}
          suite={suite}
          releaseId={id}
          canEdit={canEdit && !suite.archived_at}
        />
      ) : (
        <Text>No candidate has been prepared.</Text>
      )}
    </Stack>
  );
}
function SuiteCandidate({
  suite,
  releaseId,
  canEdit,
}: {
  suite: SuiteDetail;
  releaseId: string;
  canEdit: boolean;
}) {
  const release = useSuiteRelease(suite.id, releaseId);
  if (release.isError)
    return <SuiteError error={release.error} retry={() => void release.refetch()} />;
  if (!release.data || !release.isFetchedAfterMount) return <Text>Loading exact candidate…</Text>;
  if (release.data.schema_version !== 'automation_release_manifest.v3')
    return <HistoricalSuiteCandidate suite={suite} releaseId={releaseId} release={release.data} />;
  return <CompositionWorkspace suite={suite} releaseId={releaseId} canEdit={canEdit} />;
}

function HistoricalSuiteCandidate({
  suite,
  releaseId,
  release,
}: {
  suite: SuiteDetail;
  releaseId: string;
  release: NonNullable<ReturnType<typeof useSuiteRelease>['data']>;
}) {
  const readiness = useReleaseReadiness(suite.id, releaseId);
  const [showDocuments, setShowDocuments] = useState(false);
  const documents = useReleaseDocuments(suite.id, releaseId, showDocuments);
  return (
    <Stack>
      <Alert color="blue">
        Historical Release <Code>{releaseId}</Code> · {release.status}. This record is read-only.
        Prepare a v3 candidate for new publication.
      </Alert>
      <details>
        <summary>Retained Release record</summary>
        <Code block>{JSON.stringify(release, null, 2)}</Code>
      </details>
      {readiness.isError && (
        <SuiteError error={readiness.error} retry={() => void readiness.refetch()} />
      )}
      {readiness.data && (
        <ReleaseDocumentationReview
          releaseId={releaseId}
          documents={readiness.data.documents}
          detail={documents.data}
          showDocuments={showDocuments}
          loadingDocuments={documents.isFetching}
          documentsError={documents.error}
          onToggleDocuments={() => setShowDocuments((value) => !value)}
          onRetryDocuments={() => void documents.refetch()}
        />
      )}
      {!readiness.isError && !readiness.data && <Text>Loading Release documentation…</Text>}
    </Stack>
  );
}
